/* A quote is a Shopify draft order tagged `quote`. Nothing about a quote lives anywhere else: the customer, the lines
 * (paint carries the finder's properties), the discount, the shipping, the note. When the customer pays the invoice
 * link it becomes a real order with those properties intact, so the counter sees it exactly like a website order.
 * This file is the model the editor works on and its translation to and from the Admin API. */

import { cleanCode, stripPaintNote, withPaintNote } from './paint.js';

export const QUOTE_TAG = 'quote';
/* Options of one quote (half gallon vs gallon kit…) are separate draft orders sharing a tag qg-<first quote's id>. */
export const GROUP_PREFIX = 'qg-';
export const SENT_TAG = 'quote-sent';
export const CURRENCY = 'USD';

let seq = 0;
export const uid = () => `l${Date.now().toString(36)}${(seq++).toString(36)}`;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function money(n) {
  return (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: CURRENCY });
}

export function emptyQuote(settings) {
  const shipping = settings ? { kind: 'std', title: STD_TITLE, price: Number(settings.shipStandard) || 0, manual: false } : null;
  return { id: null, name: '', status: 'OPEN', customer: null, email: '', lines: [], discount: null, shipping, note: '', poNumber: '', tags: [QUOTE_TAG], sentAt: null, invoiceUrl: '', order: null, totals: null };
}

/* Shipping the way the store charges it (Settings ▸ General profile): Standard at a flat price, free from a threshold
   on the discounted subtotal. A price typed by hand (manual) is left alone. */
export const STD_TITLE = 'Standard shipping';
export const SHIP_KINDS = {
  std: { title: STD_TITLE },
  pickup: { title: 'Local pickup' },
  delivery: { title: 'Local delivery' },
  custom: { title: 'Shipping' },
};
export function shipKindOf(title) {
  if (!title) return 'custom';
  if (/pickup/i.test(title)) return 'pickup';
  if (/delivery/i.test(title)) return 'delivery';
  if (/standard/i.test(title)) return 'std';
  return 'custom';
}
export function standardPrice(q, settings) {
  const t = localTotals({ ...q, shipping: null });
  const freeOver = Number(settings?.shipFreeOver) || 0;
  return freeOver > 0 && t.subtotal - t.discount >= freeOver ? 0 : round2(settings?.shipStandard);
}
export function withShipping(q, settings) {
  const s = q.shipping;
  if (!s) return q;
  if (s.kind === 'pickup' && s.price !== 0) return { ...q, shipping: { ...s, price: 0 } };
  if (s.kind === 'std' && !s.manual) {
    const price = standardPrice(q, settings);
    if (price !== s.price) return { ...q, shipping: { ...s, price } };
  }
  return q;
}

export const lineTotal = (l) => round2((Number(l.unitPrice) || 0) * (Number(l.quantity) || 0));

/* The totals the editor can know before Shopify answers: Shopify adds the tax (draftOrderCalculate). */
export function localTotals(q) {
  const subtotal = round2(q.lines.reduce((s, l) => s + lineTotal(l), 0));
  let discount = 0;
  if (q.discount && Number(q.discount.value) > 0) {
    discount = q.discount.type === 'PERCENTAGE' ? round2((subtotal * Math.min(100, Number(q.discount.value))) / 100) : Math.min(subtotal, round2(q.discount.value));
  }
  const shipping = q.shipping ? round2(q.shipping.price) : 0;
  return { subtotal, discount, shipping, tax: null, total: round2(subtotal - discount + shipping), taxesIncluded: !!q.taxesIncluded };
}

const m = (amount) => ({ amount: round2(amount).toFixed(2), currencyCode: CURRENCY });

export function toDraftOrderInput(q) {
  const lineItems = q.lines.map((l) => {
    const attrs = (l.attributes || [])
      .map(({ key, value }) => ({ key, value: key === 'Paint code' ? cleanCode(value) : String(value ?? '') }))
      .filter((a) => a.key && a.value !== '');
    if (l.variantId) {
      const li = { variantId: l.variantId, quantity: Math.max(1, Math.round(l.quantity)), customAttributes: attrs };
      if (round2(l.unitPrice) !== round2(l.listPrice)) li.priceOverride = m(l.unitPrice);
      return li;
    }
    return {
      title: l.title || 'Custom item',
      quantity: Math.max(1, Math.round(l.quantity)),
      originalUnitPriceWithCurrency: m(l.unitPrice),
      taxable: l.taxable !== false,
      requiresShipping: l.requiresShipping !== false,
      customAttributes: attrs,
      ...(l.sku ? { sku: l.sku } : {}),
    };
  });
  const input = {
    lineItems,
    tags: [...new Set([...(q.tags || []), QUOTE_TAG])],
    /* what to mix goes on the order itself, where the counter reads and prints it (paint.js paintNote) */
    note: withPaintNote(q.note, q.lines),
    poNumber: q.poNumber || null,
    appliedDiscount:
      q.discount && Number(q.discount.value) > 0
        ? { value: Number(q.discount.value), valueType: q.discount.type === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED_AMOUNT', title: q.discount.title || 'Discount' }
        : null,
    shippingLine: q.shipping ? { title: q.shipping.title || 'Shipping', priceWithCurrency: m(q.shipping.price) } : null,
  };
  if (q.option && q.option.trim()) {
    input.metafields = [{ namespace: '$app', key: 'option', type: 'single_line_text_field', value: q.option.trim().slice(0, 120) }];
  }
  if (q.customer?.id) {
    input.purchasingEntity = { customerId: q.customer.id };
    input.useCustomerDefaultAddress = true;
  }
  const email = q.email || q.customer?.email;
  if (email) input.email = email;
  return input;
}

export const DRAFT_FIELDS = `
  id name status createdAt updatedAt invoiceUrl invoiceSentAt completedAt note2 poNumber tags email
  customer { id displayName firstName lastName email phone defaultAddress { company city provinceCode } }
  appliedDiscount { title value valueType }
  shippingLine { title originalPriceSet { shopMoney { amount } } }
  subtotalPriceSet { shopMoney { amount } }
  totalDiscountsSet { shopMoney { amount } }
  totalShippingPriceSet { shopMoney { amount } }
  totalTaxSet { shopMoney { amount } }
  totalPriceSet { shopMoney { amount } }
  taxesIncluded
  order { id name }
  sent: metafield(namespace: "$app", key: "sent") { value }
  option: metafield(namespace: "$app", key: "option") { value }
  lineItems(first: 100) {
    nodes {
      uuid title variantTitle sku quantity custom taxable requiresShipping
      customAttributes { key value }
      originalUnitPriceWithCurrency { amount }
      priceOverride { amount }
      approximateDiscountedUnitPriceSet { shopMoney { amount } }
      image { url(transform: { maxWidth: 120, maxHeight: 120 }) }
      variant { id price image { url(transform: { maxWidth: 120, maxHeight: 120 }) } product { title featuredMedia { preview { image { url(transform: { maxWidth: 120, maxHeight: 120 }) } } } } }
    }
  }`;

const amt = (set) => Number(set?.shopMoney?.amount ?? set?.amount ?? 0);

export function customerOf(c) {
  if (!c) return null;
  return {
    id: c.id,
    displayName: c.displayName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Customer',
    firstName: c.firstName || '',
    email: c.email || '',
    phone: c.phone || '',
    company: c.defaultAddress?.company || '',
    place: [c.defaultAddress?.city, c.defaultAddress?.provinceCode].filter(Boolean).join(', '),
  };
}

export function fromDraftOrder(d) {
  let sent = null;
  try { sent = d.sent?.value ? JSON.parse(d.sent.value) : null; } catch { sent = null; }
  const lines = (d.lineItems?.nodes || []).map((li) => {
    const attrs = (li.customAttributes || []).map(({ key, value }) => ({ key, value }));
    const isPaint = attrs.some((a) => a.key === 'Paint code');
    const hex = attrs.find((a) => a.key === '_hex')?.value;
    const list = li.variant ? Number(li.variant.price) : Number(li.originalUnitPriceWithCurrency?.amount || 0);
    const unit = li.priceOverride ? Number(li.priceOverride.amount) : Number(li.originalUnitPriceWithCurrency?.amount ?? list);
    return {
      uid: li.uuid || uid(),
      kind: isPaint ? 'paint' : li.variant ? 'product' : 'custom',
      variantId: li.variant?.id || null,
      title: li.variant?.product?.title || li.title,
      variantTitle: li.variantTitle && li.variantTitle !== 'Default Title' ? li.variantTitle : '',
      sku: li.sku || '',
      quantity: li.quantity,
      listPrice: list,
      unitPrice: unit,
      taxable: li.taxable,
      requiresShipping: li.requiresShipping,
      attributes: attrs,
      hex: isPaint ? hex : undefined,
      image: li.image?.url || li.variant?.image?.url || li.variant?.product?.featuredMedia?.preview?.image?.url || '',
    };
  });
  const disc = d.appliedDiscount;
  return {
    id: d.id,
    name: d.name,
    status: d.status,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    customer: customerOf(d.customer),
    email: d.email || d.customer?.email || '',
    lines,
    discount: disc ? { type: disc.valueType === 'PERCENTAGE' ? 'PERCENTAGE' : 'FIXED_AMOUNT', value: Number(disc.value), title: disc.title || '' } : null,
    shipping: d.shippingLine ? { kind: shipKindOf(d.shippingLine.title), title: d.shippingLine.title, price: amt(d.shippingLine.originalPriceSet), manual: true } : null,
    note: stripPaintNote(d.note2),
    poNumber: d.poNumber || '',
    tags: d.tags || [],
    invoiceUrl: d.invoiceUrl || '',
    sentAt: sent?.at || d.invoiceSentAt || null,
    option: d.option?.value || '',
    group: (d.tags || []).find((t) => t.startsWith(GROUP_PREFIX)) || '',
    sent,
    order: d.order || null,
    totals: {
      subtotal: amt(d.subtotalPriceSet),
      discount: amt(d.totalDiscountsSet),
      shipping: amt(d.totalShippingPriceSet),
      tax: amt(d.totalTaxSet),
      total: amt(d.totalPriceSet),
      taxesIncluded: !!d.taxesIncluded,
    },
  };
}

/* What the list shows: Paid (it became an order), Sent, or Draft. */
export function quoteState(q) {
  if (q.status === 'COMPLETED') return { label: q.order ? `Paid · ${q.order.name}` : 'Paid', tone: 'success' };
  if (q.sentAt || q.status === 'INVOICE_SENT' || (q.tags || []).includes(SENT_TAG)) return { label: 'Sent', tone: 'info' };
  return { label: 'Draft', tone: 'neutral' };
}

export const quoteNumber = (q) => (q?.name ? q.name.replace(/^#/, '') : 'New');

export function daysAgo(iso, now = Date.now()) {
  if (!iso) return '';
  const d = Math.floor((now - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
